import {logger} from '../../../logger';
import React from 'react';
import {observable, action, computed, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import PaymentAddr from './paymentAddr';
import store from '../../s';
import PaymentCodes from './paymentCodes';
import {checkout} from '../../../api';
import ModalPromo from '../modals/promo';
import {Portal} from '../../../util';
import {ICheckoutMessageType, ICheckoutStep, ICheckoutType} from '../../i';
import {isCheckoutRedesign} from '../../ab-redesign';
import {AbStep} from '../ab-step/ab-redesign-step';
import {showsBillingStep} from './ab-redesign-billing';

declare var modalOpen: any;
declare var OpenLink: any;

@observer
class PaymentComponent extends React.Component {
    @observable showCodes: boolean;
    @observable hidePayment: boolean;
    @observable placingCCOrder: boolean = false;
    @observable test: boolean = false;

    constructor(props) {
        super(props);
        this.placeOrder = this.placeOrder.bind(this);
        this.placeOrderMyFrames = this.placeOrderMyFrames.bind(this);
        this.placingCCOrder = false;
        this.hidePayment = false;
        this.reloadPayment = this.reloadPayment.bind(this);
        store.checkoutReloadPayment = this.reloadPayment;
        makeObservable(this);
    }

    @action
    placeOrder = async () => {
        this.placingCCOrder = true;
        store.uiCheckout.processing = true;
        setTimeout(function () {
            store.uiCheckout.processing = false;
        }, 20000);

        try {
            const success = await checkout('cc-place-order');
            runInAction(() => {
                // approved ria
                this.placingCCOrder = false;
                if (success && store.checkout.cstep === ICheckoutStep.success) {
                    location.reload();
                } else {
                    store.uiCheckout.processing = false;
                }
            });
        } catch (_err) {
            runInAction(() => {
                // approved ria
                logger.error(_err);
                store.uiCheckout.processing = false;
                this.placingCCOrder = false;
            });
        }
    };

    @action
    placeOrderMyFrames = async () => {
        store.uiCheckout.processing = true;
        const success = await checkout('myf-place-order');
        store.uiCheckout.processing = false;
        if (store.checkout.cstep === ICheckoutStep.success) {
            OpenLink('/view-order');
        }
    };

    @action
    reloadPayment = async () => {
        if (store.checkout.CheckoutType !== ICheckoutType.paypal_express) {
            this.hidePayment = true;
            setTimeout(() => {
                this.hidePayment = false;
            }, 200);
        }
    };

    @action
    reloadPaymentFailure = async () => {
        if (store.checkout.CheckoutType !== ICheckoutType.paypal_express) {
            this.hidePayment = true;
            setTimeout(() => {
                this.hidePayment = false;
            }, 200);
        }
    };

    @computed get isTestOrder() {
        try {
            return store.checkout.notes_customer.toLowerCase().includes('marketing-test-order') || store.checkout.notes_gift.toLowerCase().includes('marketing-test-order');
        } catch (err5) {
            return false;
        }
    }

    // The control keeps its .payment-codes block; the redesign uses the same wrapper and classes as
    // the "Pay Now" button so the two look alike. `hidden` keeps the wrapper while the order is placing.
    renderPlaceOrderButton(label: string, ariaLabel: string, onClick: () => void, hidden: boolean = false) {
        const redesign = isCheckoutRedesign(store.checkout.isMyFrames);
        const btn = !hidden && (
            <button
                type="button"
                className={`${redesign ? '' : 'btn '}btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                aria-label={ariaLabel}
                onClick={onClick}
            >
                {label}
            </button>
        );
        if (redesign) {
            return <div className="ab-step-actions ab-place-order">{btn}</div>;
        }
        return (
            <div className="payment-codes">
                <div className="row">
                    <div className="col-12 text-center">{btn}</div>
                </div>
            </div>
        );
    }

    render() {
        const redesign = isCheckoutRedesign(store.checkout.isMyFrames);
        // Control only: its promo-covered $0 total is placed with 'myf-place-order'. The redesign never renders that
        // button; a covered total (gift card or promo code) stays on PaypalAdv, whose "Place Order" calls /api/pay/create
        // (skipPayment -> 'gift' order, shipping and tax kept), while 'myf-place-order' would zero them.
        const zeroTotalCode = store.checkout.code_promo.value && store.checkout.code_promo.value.length > 0;
        return (
            <div>
                {!redesign && (
                    <div className="row">
                        <div className="col-12">
                            <PaymentAddr />
                        </div>
                    </div>
                )}

                {redesign && store.checkout.cstep === ICheckoutStep.payment && !store.checkout.isMyFrames && Portal?.PortalID !== 3 && (
                    <div className="ab-gift-card">
                        {!this.showCodes && !store.checkout.code_gift_applied && (
                            <button
                                className="ab-gift-toggle"
                                type="button"
                                onClick={() => (this.showCodes = true)}
                            >
                                Have a gift card?
                            </button>
                        )}
                        {(this.showCodes || store.checkout.code_gift_applied) && (
                            <PaymentCodes
                                reloadPayment={this.reloadPayment}
                                reloadPaymentFailure={this.reloadPaymentFailure}
                                promoOnly={false}
                                giftOnly={true}
                            />
                        )}
                    </div>
                )}

                {store.checkout.cstep === ICheckoutStep.paymentAddr && !store.checkout.isMyFrames && !redesign && (
                    <div className="payment-codes">
                        <div className="row">
                            <div className="col-12">
                                <button
                                    className="link text-btn"
                                    type="button"
                                    aria-label="Enter gift card or promo code button"
                                    onClick={() => modalOpen('modal-checkout-promo')}
                                >
                                    + Do you have a {Portal?.PortalID === 3 ? 'promo code' : 'gift card or promo code'}?{' '}
                                    <span style={{paddingLeft: 4}}>
                                        <i
                                            className="fas fa-tags"
                                            aria-hidden={true}
                                        />
                                    </span>
                                </button>
                            </div>
                        </div>
                        <ModalPromo
                            reloadPayment={this.reloadPayment}
                            reloadPaymentFailure={this.reloadPaymentFailure}
                            modalId="modal-checkout-promo"
                        />
                    </div>
                )}

                {store.checkout.cstep === ICheckoutStep.payment && !store.checkout.isMyFrames && !redesign && (
                    <div className="payment-codes">
                        <div className="row">
                            <div className="col-12">
                                {!this.showCodes && (
                                    <div>
                                        <button
                                            className="link text-btn"
                                            type="button"
                                            aria-label="Enter gift card or promo code button"
                                            onClick={() => (this.showCodes = true)}
                                        >
                                            + Do you have a {Portal?.PortalID === 3 ? 'promo code' : 'gift card or promo code'}?{' '}
                                            <span style={{paddingLeft: 4}}>
                                                <i
                                                    className="fas fa-tags"
                                                    aria-hidden={true}
                                                />
                                            </span>
                                        </button>
                                    </div>
                                )}
                                {this.showCodes && (
                                    <div className="col-12">
                                        <PaymentCodes
                                            reloadPayment={this.reloadPayment}
                                            reloadPaymentFailure={this.reloadPaymentFailure}
                                            promoOnly={false}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* {store.checkout.cstep === ICheckoutStep.payment && !(store.checkout.code_gift_applied && store.checkout.cart.pricing.total <= 0) && !(store.checkout.code_promo_applied && store.checkout.cart.pricing.total <= 0) && <PaymentMethod />} */}

                {store.checkout.messages
                    .filter(o => o.msgType === ICheckoutMessageType.paypal || o.msgType === ICheckoutMessageType.stripe || o.msgType === ICheckoutMessageType.generic)
                    .map((o, i) => (
                        <div className="row">
                            <div className="col-12">
                                <div
                                    key={i}
                                    className="alert alert-danger form-error"
                                    role="alert"
                                >
                                    <div className="error-title">Payment Error</div>
                                    <div>{o.msg && o.msg.toString()}</div>
                                </div>
                            </div>
                        </div>
                    ))}

                {store.checkout.ship.messages
                    .filter(o => o.msgType === ICheckoutMessageType.paypal || o.msgType === ICheckoutMessageType.stripe || o.msgType === ICheckoutMessageType.generic)
                    .map((o, i) => (
                        <div className="row">
                            <div className="col-12">
                                <div
                                    key={i}
                                    className="alert alert-danger form-error"
                                    role="alert"
                                >
                                    <div className="error-title">Payment Error (Shipping)</div>
                                    <div>{o.msg && o.msg.toString()}</div>
                                </div>
                            </div>
                        </div>
                    ))}

                {store.checkout.billing.messages
                    .filter(o => o.msgType === ICheckoutMessageType.paypal || o.msgType === ICheckoutMessageType.stripe || o.msgType === ICheckoutMessageType.generic)
                    .map((o, i) => (
                        <div className="row">
                            <div className="col-12">
                                <div
                                    key={i}
                                    className="alert alert-danger form-error"
                                    role="alert"
                                >
                                    <div className="error-title">Payment Error (Billing)</div>
                                    <div>{o.msg && o.msg.toString()}</div>
                                </div>
                            </div>
                        </div>
                    ))}

                {store.checkout.isMyFrames && store.checkout.cstep === ICheckoutStep.payment && this.renderPlaceOrderButton('Place MyFrames Order', 'Place MyFrames Order', this.placeOrderMyFrames)}
                {!redesign &&
                    !store.checkout.isMyFrames &&
                    store.checkout.cstep === ICheckoutStep.payment &&
                    store.checkout.cart.pricing.total <= 0 &&
                    (store.checkout.cart.pricing.discount != 0 || store.checkout.cart.pricing.gift != 0) &&
                    zeroTotalCode &&
                    this.renderPlaceOrderButton('Place Order', 'Place Order', this.placeOrderMyFrames, store.uiCheckout.processing)}
                {this.isTestOrder && store.checkout.cstep === ICheckoutStep.payment && this.renderPlaceOrderButton('Place Test Order', 'Place Order', this.placeOrderMyFrames, store.uiCheckout.processing)}
            </div>
        );
    }
}

@observer
class PaymentComponentContainer extends React.Component<{children?: React.ReactNode}> {
    constructor(props) {
        super(props);
    }

    renderRedesign() {
        // paymentAddr (8) is numerically after payment (7): the Payment card stays pending while the Billing address card is active.
        const active = store.checkout.cstep >= ICheckoutStep.payment && store.checkout.cstep !== ICheckoutStep.paymentAddr;
        return (
            <div className="payment">
                <AbStep
                    className="ab-payment"
                    // Step 4 when the billing address is the shipping address (no Billing address card), 5 otherwise.
                    num={showsBillingStep() ? 5 : 4}
                    title="Payment"
                    state={active ? 'active' : 'pending'}
                >
                    {active && (
                        <div className="ab-step-body">
                            <PaymentComponent />
                            {store.checkout.messages
                                .filter(o => o.msgType === ICheckoutMessageType.payment)
                                .map((o, i) => (
                                    <div
                                        key={i}
                                        className="alert alert-danger form-error"
                                        role="alert"
                                    >
                                        <div className="error-title">Payment Error</div>
                                        <div>{o.msg && o.msg.toString()}</div>
                                    </div>
                                ))}
                            {this.props.children}
                        </div>
                    )}
                </AbStep>
            </div>
        );
    }

    render() {
        if (isCheckoutRedesign(store.checkout.isMyFrames)) {
            return this.renderRedesign();
        }
        return (
            <div className="payment">
                <div
                    className="checkout-header"
                    style={{color: Portal?.PortalID === 3 ? '#33393a' : '', backgroundColor: Portal?.PortalID === 3 ? '#edece9' : ''}}
                >
                    3. {store.checkout.isMyFrames ? 'Place Order' : 'Payment Information'}
                    {store.checkout.cstep >= ICheckoutStep.payment && (
                        <span
                            className="fas fa-caret-down"
                            aria-hidden={true}
                            style={{
                                fontSize: 20,
                                paddingRight: 12,
                                marginTop: 14,
                                float: 'right',
                                color: '#FFF',
                            }}
                        />
                    )}
                </div>

                {store.checkout.cstep >= ICheckoutStep.payment && <PaymentComponent />}
                <div
                    className="checkout-header"
                    style={{marginTop: 12, color: Portal?.PortalID === 3 ? '#33393a' : '', backgroundColor: Portal?.PortalID === 3 ? '#edece9' : ''}}
                >
                    4. Payment
                </div>

                {store.checkout.messages
                    .filter(o => o.msgType === ICheckoutMessageType.payment)
                    .map((o, i) => (
                        <div className="row">
                            <div className="col-12">
                                <div
                                    key={i}
                                    className="alert alert-danger form-error"
                                    role="alert"
                                >
                                    <div className="error-title">Payment Error</div>
                                    <div>{o.msg && o.msg.toString()}</div>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
        );
    }
}

export default PaymentComponentContainer;
